import React, { useCallback } from 'react';
import { Box, Typography, IconButton, Divider } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Title,
} from '@mui/icons-material';

interface SimpleRichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
}

const SimpleRichTextEditor: React.FC<SimpleRichTextEditorProps> = ({
  value,
  onChange,
  label,
  placeholder,
  required = false,
}) => {
  const editorRef = React.useRef<HTMLDivElement>(null);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const execCommand = useCallback(
    (command: string, value?: string) => {
      document.execCommand(command, false, value);
      editorRef.current?.focus();
      handleInput();
    },
    [handleInput]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        execCommand('insertHTML', '<br><br>');
      }
    },
    [execCommand]
  );

  React.useEffect(() => {
    if (editorRef.current) {
      const currentContent = editorRef.current.innerHTML;
      const newValue = value || '';
      if (currentContent !== newValue) {
        editorRef.current.innerHTML = newValue;
      }
    }
  }, [value]);

  return (
    <Box>
      {label && (
        <Typography
          variant="body2"
          sx={{
            mb: 1,
            fontWeight: 500,
            color: 'text.primary',
          }}
        >
          {label}
          {required && ' *'}
        </Typography>
      )}

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          p: 1,
          border: '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: '4px 4px 0 0',
          backgroundColor: '#f5f5f5',
        }}
      >
        <IconButton size="small" onClick={() => execCommand('bold')} title="Negrita">
          <FormatBold fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => execCommand('italic')} title="Cursiva">
          <FormatItalic fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => execCommand('underline')} title="Subrayado">
          <FormatUnderlined fontSize="small" />
        </IconButton>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <IconButton size="small" onClick={() => execCommand('formatBlock', 'h3')} title="Título">
          <Title fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => execCommand('insertUnorderedList')}
          title="Lista con viñetas"
        >
          <FormatListBulleted fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => execCommand('insertOrderedList')}
          title="Lista numerada"
        >
          <FormatListNumbered fontSize="small" />
        </IconButton>
      </Box>

      {/* Editor */}
      <Box
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        sx={{
          minHeight: '120px',
          p: 1.5,
          border: '1px solid rgba(0, 0, 0, 0.23)',
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          fontSize: '14px',
          lineHeight: 1.6,
          outline: 'none',
          '&:focus': {
            borderColor: 'primary.main',
          },
          '&:empty:before': {
            content: `"${placeholder || ''}"`,
            color: 'text.secondary',
            fontStyle: 'italic',
          },
          '& h3': {
            fontSize: '1.1rem',
            fontWeight: 600,
            margin: '8px 0',
          },
          '& ul, & ol': {
            margin: '8px 0',
            paddingLeft: '20px',
          },
          '& li': {
            margin: '4px 0',
          },
        }}
        suppressContentEditableWarning
      />
    </Box>
  );
};

export default SimpleRichTextEditor;
